import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation, languages } from '../lib/i18n';
import { authAPI } from '../lib/api';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { User, Globe, DollarSign, Save, Moon, Sun, Palette, Trash2, Download, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import TwoFactorSetup from '../components/TwoFactorSetup';
import AccountModeSettings from '../components/AccountModeSettings';
import PushNotificationSettings from '../components/PushNotificationSettings';

const Settings = () => {
  const { user, language, theme, updateUser, changeLanguage, changeTheme, toggleTheme, logout } = useAuth();
  const t = useTranslation(language);

  const [form, setForm] = useState({
    name: user?.name || '',
    currency: user?.currency || 'EUR',
    language: user?.language || 'es'
  });
  const [saving, setSaving] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);

  const currencies = [
    { code: 'EUR', symbol: '€', name: 'Euro' },
    { code: 'USD', symbol: '$', name: 'US Dollar' },
    { code: 'GBP', symbol: '£', name: 'British Pound' },
    { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc' },
    { code: 'DOP', symbol: 'RD$', name: 'Peso Dominicano' },
    { code: 'COP', symbol: '$', name: 'Peso Colombiano' },
    { code: 'MXN', symbol: '$', name: 'Peso Mexicano' },
    { code: 'ARS', symbol: '$', name: 'Peso Argentino' },
    { code: 'CAD', symbol: 'C$', name: 'Dólar Canadiense' },
    { code: 'BRL', symbol: 'R$', name: 'Real Brasileño' },
    { code: 'CLP', symbol: '$', name: 'Peso Chileno' },
    { code: 'PEN', symbol: 'S/', name: 'Sol Peruano' },
    { code: 'UYU', symbol: '$U', name: 'Peso Uruguayo' },
    { code: 'JPY', symbol: '¥', name: 'Yen Japonés' },
    { code: 'CNY', symbol: '¥', name: 'Yuan Chino' },
    { code: 'INR', symbol: '₹', name: 'Rupia India' },
    { code: 'AUD', symbol: 'A$', name: 'Dólar Australiano' },
    { code: 'SEK', symbol: 'kr', name: 'Corona Sueca' },
  ];

  const handleSave = async () => {
    setSaving(true);
    try {
      await authAPI.updateProfile({
        name: form.name,
        currency: form.currency,
        language: form.language
      });

      updateUser({
        name: form.name,
        currency: form.currency,
        language: form.language
      });

      toast.success('Configuración guardada');
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleLanguageChange = (newLang) => {
    setForm(prev => ({ ...prev, language: newLang }));
    changeLanguage(newLang);
  };

  const handleExportData = async () => {
    try {
      const res = await api.get('/account/export-data');
      const dataStr = JSON.stringify(res.data, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `trebol_data_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success('Datos exportados correctamente');
    } catch (error) {
      toast.error('Error al exportar datos');
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirm !== 'ELIMINAR') {
      toast.error('Escribe ELIMINAR para confirmar');
      return;
    }

    setDeleting(true);
    try {
      await api.delete('/account/delete');
      toast.success('Cuenta eliminada');
      logout();
    } catch (error) {
      toast.error('Error al eliminar cuenta');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="font-display font-bold text-2xl lg:text-3xl text-foreground tracking-tight">
          {t('settings')}
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Personaliza tu experiencia en Trebol Finance by Leapifyes
        </p>
      </div>

      {/* Profile Section */}
      <Card className="border-border rounded-sm shadow-none" data-testid="profile-card">
        <CardHeader>
          <CardTitle className="text-sm font-semibold text-foreground flex items-center gap-2">
            <User size={18} className="text-primary" />
            {t('profile')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">
              {t('name')}
            </Label>
            <Input
              value={form.name}
              onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
              className="rounded-sm"
              data-testid="settings-name-input"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">
              {t('email')}
            </Label>
            <Input
              value={user?.email || ''}
              disabled
              className="rounded-sm bg-muted"
            />
            <p className="text-xs text-muted-foreground">El email no puede ser modificado</p>
          </div>
        </CardContent>
      </Card>

      {/* Theme Section */}
      <Card className="border-border rounded-sm shadow-none" data-testid="theme-card">
        <CardHeader>
          <CardTitle className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Palette size={18} className="text-primary" />
            {t('theme') || 'Tema'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label className="text-sm font-medium text-foreground">
                Modo Oscuro
              </Label>
              <p className="text-xs text-muted-foreground">
                Cambia entre modo claro y oscuro
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Sun size={18} className={cn(
                "transition-colors",
                theme === 'light' ? "text-amber-500" : "text-muted-foreground"
              )} />
              <Switch
                checked={theme === 'dark'}
                onCheckedChange={toggleTheme}
                data-testid="theme-toggle"
              />
              <Moon size={18} className={cn(
                "transition-colors",
                theme === 'dark' ? "text-blue-400" : "text-muted-foreground"
              )} />
            </div>
          </div>

          {/* Theme Preview */}
          <div className="grid grid-cols-2 gap-3 pt-2">
            <button
              onClick={() => changeTheme('light')}
              className={cn(
                "p-4 rounded-sm border-2 transition-all",
                theme === 'light'
                  ? "border-primary bg-white"
                  : "border-border bg-white/50 hover:border-muted-foreground"
              )}
            >
              <div className="flex items-center gap-2 mb-2">
                <Sun size={16} className="text-amber-500" />
                <span className="text-sm font-medium text-slate-900">Claro</span>
              </div>
              <div className="space-y-1">
                <div className="h-2 bg-slate-200 rounded-full w-full" />
                <div className="h-2 bg-slate-100 rounded-full w-3/4" />
              </div>
            </button>

            <button
              onClick={() => changeTheme('dark')}
              className={cn(
                "p-4 rounded-sm border-2 transition-all",
                theme === 'dark'
                  ? "border-primary bg-slate-900"
                  : "border-border bg-slate-900/90 hover:border-muted-foreground"
              )}
            >
              <div className="flex items-center gap-2 mb-2">
                <Moon size={16} className="text-blue-400" />
                <span className="text-sm font-medium text-white">Oscuro</span>
              </div>
              <div className="space-y-1">
                <div className="h-2 bg-slate-700 rounded-full w-full" />
                <div className="h-2 bg-slate-800 rounded-full w-3/4" />
              </div>
            </button>
          </div>
        </CardContent>
      </Card>

      {/* 2FA Security Section */}
      <TwoFactorSetup />

      {/* Account Mode Section */}
      <AccountModeSettings />

      {/* Currency Section */}
      <Card className="border-border rounded-sm shadow-none" data-testid="currency-card">
        <CardHeader>
          <CardTitle className="text-sm font-semibold text-foreground flex items-center gap-2">
            <DollarSign size={18} className="text-primary" />
            {t('currency')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">
              Moneda Principal
            </Label>
            <select
              value={form.currency}
              onChange={(e) => setForm(prev => ({ ...prev, currency: e.target.value }))}
              data-testid="settings-currency-select"
              style={{ width: '100%', padding: '10px 12px', borderRadius: '4px', border: '1px solid #e2e8f0', fontSize: '14px', color: '#0f172a', backgroundColor: '#ffffff', appearance: 'auto' }}
            >
              {currencies.map(c => (
                <option key={c.code} value={c.code}>{c.symbol} {c.name}</option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Language Section */}
      <Card className="border-border rounded-sm shadow-none" data-testid="language-card">
        <CardHeader>
          <CardTitle className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Globe size={18} className="text-primary" />
            {t('language')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">
              Idioma de la Interfaz
            </Label>
            <select
              value={form.language}
              onChange={(e) => handleLanguageChange(e.target.value)}
              data-testid="settings-language-select"
              style={{ width: '100%', padding: '10px 12px', borderRadius: '4px', border: '1px solid #e2e8f0', fontSize: '14px', color: '#0f172a', backgroundColor: '#ffffff', appearance: 'auto' }}
            >
              {languages.map(l => (
                <option key={l.code} value={l.code}>{l.name}</option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <Button
        onClick={handleSave}
        disabled={saving}
        className="w-full bg-primary hover:bg-primary/90 rounded-sm"
        data-testid="save-settings-btn"
      >
        <Save size={16} className="mr-2" />
        {saving ? 'Guardando...' : 'Guardar Configuración'}
      </Button>

      {/* Push Notifications */}
      <PushNotificationSettings />

      {/* Account Management (GDPR) */}
      <Card className="border-border rounded-sm shadow-none" data-testid="account-management-card">
        <CardHeader>
          <CardTitle className="text-sm font-semibold text-foreground flex items-center gap-2">
            <User size={18} className="text-primary" />
            Gestión de Cuenta
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Export Data */}
          <div className="flex items-center justify-between p-4 rounded-sm border border-border">
            <div>
              <p className="font-medium text-foreground text-sm">Exportar mis datos</p>
              <p className="text-xs text-muted-foreground">Descarga todos tus datos en formato JSON</p>
            </div>
            <Button variant="outline" onClick={handleExportData} className="rounded-sm">
              <Download size={16} className="mr-2" />
              Exportar
            </Button>
          </div>

          {/* Delete Account */}
          <div className="flex items-center justify-between p-4 rounded-sm border border-red-200 dark:border-red-900 bg-red-50/50 dark:bg-red-950/20">
            <div>
              <p className="font-medium text-red-700 dark:text-red-400 text-sm">Eliminar cuenta</p>
              <p className="text-xs text-red-600 dark:text-red-500">Esta acción es irreversible</p>
            </div>
            <Button
              variant="destructive"
              onClick={() => setShowDeleteDialog(true)}
              className="rounded-sm"
            >
              <Trash2 size={16} className="mr-2" />
              Eliminar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* App Info */}
      <Card className="border-border rounded-sm shadow-none bg-muted/50">
        <CardContent className="p-4 text-center">
          <p className="text-xs text-muted-foreground">
            Trebol Finance by Leapifyes v1.0.0
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Control total de tus finanzas personales - DigitalLeap Solutions S.L.
          </p>
        </CardContent>
      </Card>

      {/* Delete Account Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle size={20} />
              Eliminar cuenta
            </DialogTitle>
          </DialogHeader>

          <div className="py-4 space-y-4">
            <div className="p-4 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-sm">
              <p className="text-sm text-red-700 dark:text-red-400">
                <strong>¡Atención!</strong> Esta acción eliminará permanentemente:
              </p>
              <ul className="list-disc list-inside text-sm text-red-600 dark:text-red-500 mt-2 space-y-1">
                <li>Tu cuenta y perfil</li>
                <li>Todas tus transacciones</li>
                <li>Tus cuentas, categorías y presupuestos</li>
                <li>Tus inversiones y objetivos de ahorro</li>
                <li>Todo tu historial</li>
              </ul>
            </div>

            <div className="space-y-2">
              <Label className="text-sm">Escribe <strong>ELIMINAR</strong> para confirmar:</Label>
              <Input
                value={deleteConfirm}
                onChange={(e) => setDeleteConfirm(e.target.value)}
                placeholder="ELIMINAR"
                className="rounded-sm"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)} className="rounded-sm">
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteAccount}
              disabled={deleting || deleteConfirm !== 'ELIMINAR'}
              className="rounded-sm"
            >
              {deleting ? 'Eliminando...' : 'Eliminar definitivamente'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Settings;
